import { cancelAnimations, playAnimation, settleAnimations } from '../../components/motion-runner.js';
import { canAnimateElement, readCssTimeMs } from '../../components/motion-policy.js';
import { createModalFocusScope } from '../../components/modal-focus-scope.js';
import { acquirePagedSearchBusy } from '../../components/paged-search-surface-busy.js';
import { showToast as showSharedToast } from '../../components/toast.js';
import { resolveCsrfToken, sendJsonMutation } from '../../components/write-request.js';

const MOVE_UNKNOWN_MESSAGE = 'Could not confirm the new position was saved. Reload the page before trying again.';
const MOVE_REFRESH_MESSAGE = 'Position saved, but the list could not be refreshed. Reload the page.';
const REMOVE_UNKNOWN_MESSAGE = 'Could not confirm the game was removed. Reload the page before trying again.';
const REMOVE_REFRESH_MESSAGE = 'Removed, but the list could not be refreshed. Reload the page.';

// Where focus lands after a card left the list, chosen from the swapped-in page: the `⋯` of the card now in the
// removed one's place, else of the previous card; when the page stepped back, the `⋯` of its last card; with no
// cards left, the empty list's `Add game`.
export function resolveRemoveSuccessorFocus({ removedIndex, previousPage, currentPage, cards, emptyStateAction }) {
    if (cards.length === 0) {
        return emptyStateAction ?? null;
    }

    const successor = currentPage < previousPage
        ? cards[cards.length - 1]
        : cards[Math.min(Math.max(removedIndex, 0), cards.length - 1)];

    return successor?.querySelector('[data-lists-card-menu-trigger]') ?? null;
}

export function initManualCardEditFlow(
    documentRef = globalThis.document,
    fetchImpl = globalThis.fetch,
    manualGamesBrowserController = undefined,
    timerApi = globalThis,
    showToast = (message) => showSharedToast(message, documentRef, globalThis.window, timerApi),
) {
    if (typeof fetchImpl !== 'function') {
        return;
    }

    const csrfToken = resolveCsrfToken(documentRef);
    if (csrfToken === '') {
        return;
    }

    let activeCard = null;
    let activeMenuCard = null;
    let activeRemoveConfirmCard = null;
    let mutationInFlight = false;
    // The card whose write is out; separate from `mutationState`, which only drives its motion.
    let pendingCard = null;
    // A card control that held focus when it was disabled, given focus back once the card is usable again.
    let parkedFocus = null;
    let activeSheetCard = null;
    let menuOpenFocusOrigin = null;
    let menuOpenedByKeyboard = false;
    let removeConfirmScope = null;

    const sheetMotionState = new WeakMap();
    const confirmMotionState = new WeakMap();

    const readOrderRoot = () => {
        const root = documentRef?.querySelector?.('[data-lists-manual-order]');
        return root instanceof HTMLElement ? root : null;
    };

    const readVisibleCards = () => {
        return Array.from(documentRef?.querySelectorAll?.('[data-lists-manual-order] [data-lists-manual-card]') ?? [])
            .filter((card) => card instanceof HTMLElement);
    };

    // The page's cards, in order; they sit in the browser's results, not inside the order root.
    const readBrowserCards = () => Array.from(
        documentRef?.querySelectorAll?.('[data-lists-manual-browser] [data-lists-manual-card]') ?? [],
    ).filter((card) => card instanceof HTMLElement);

    const readEditor = (card) => {
        if (!(card instanceof HTMLElement)) {
            return null;
        }

        const editor = card.querySelector('[data-lists-card-editor]');
        return editor instanceof HTMLElement ? editor : null;
    };

    const readSheet = (card) => {
        if (!(card instanceof HTMLElement)) {
            return null;
        }

        const sheet = card.querySelector('[data-lists-card-sheet]');
        return sheet instanceof HTMLElement ? sheet : null;
    };

    const readSheetPanel = (card) => {
        const sheet = readSheet(card);
        if (!(sheet instanceof HTMLElement)) {
            return null;
        }

        const panel = sheet.querySelector('[data-lists-card-sheet-panel]');
        return panel instanceof HTMLElement ? panel : null;
    };

    const readSheetBackdrop = (card) => {
        const sheet = readSheet(card);
        if (!(sheet instanceof HTMLElement)) {
            return null;
        }

        const backdrop = sheet.querySelector('[data-lists-card-menu-dismiss][tabindex="-1"]');
        return backdrop instanceof HTMLElement ? backdrop : null;
    };

    const readMenu = (card) => {
        if (!(card instanceof HTMLElement)) {
            return null;
        }

        const menu = card.querySelector('[data-lists-card-menu]');
        return menu instanceof HTMLElement ? menu : null;
    };

    const readSheetView = (card, state) => {
        if (state === 'menu') {
            return readMenu(card);
        }

        if (state === 'editor') {
            return readEditor(card);
        }

        return null;
    };

    const readMenuTrigger = (card) => {
        if (!(card instanceof HTMLElement)) {
            return null;
        }

        const trigger = card.querySelector('[data-lists-card-menu-trigger]');
        return trigger instanceof HTMLElement ? trigger : null;
    };

    const readPositionInput = (card) => {
        if (!(card instanceof HTMLElement)) {
            return null;
        }

        const input = card.querySelector('[data-lists-position-input]');
        return input instanceof HTMLInputElement ? input : null;
    };

    const readEditorErrorElement = (card) => {
        if (!(card instanceof HTMLElement)) {
            return null;
        }

        const error = card.querySelector('[data-lists-card-error]');
        return error instanceof HTMLElement ? error : null;
    };

    const readRemoveConfirm = (card) => {
        if (!(card instanceof HTMLElement)) {
            return null;
        }

        const confirmSurface = card.querySelector('[data-lists-card-remove-confirm]');
        return confirmSurface instanceof HTMLElement ? confirmSurface : null;
    };

    const readRemoveConfirmPanel = (card) => {
        const confirmSurface = readRemoveConfirm(card);
        if (!(confirmSurface instanceof HTMLElement)) {
            return null;
        }

        const panel = confirmSurface.querySelector('[data-lists-card-remove-confirm-panel]');
        return panel instanceof HTMLElement ? panel : null;
    };

    const readRemoveConfirmBackdrop = (card) => {
        const confirmSurface = readRemoveConfirm(card);
        if (!(confirmSurface instanceof HTMLElement)) {
            return null;
        }

        const backdrop = confirmSurface.querySelector('[data-lists-card-remove-cancel][tabindex="-1"]');
        return backdrop instanceof HTMLElement ? backdrop : null;
    };

    const readRemoveErrorElement = (card) => {
        if (!(card instanceof HTMLElement)) {
            return null;
        }

        const error = card.querySelector('[data-lists-card-remove-error]');
        return error instanceof HTMLElement ? error : null;
    };

    const clearErrorElement = (errorElement) => {
        if (!(errorElement instanceof HTMLElement)) {
            return;
        }

        errorElement.hidden = true;
        errorElement.textContent = '';
    };

    const clearEditorError = (card) => {
        clearErrorElement(readEditorErrorElement(card));
    };

    const clearRemoveError = (card) => {
        clearErrorElement(readRemoveErrorElement(card));
    };

    const showErrorElement = (errorElement, message) => {
        if (!(errorElement instanceof HTMLElement)) {
            return;
        }

        errorElement.textContent = message;
        errorElement.hidden = false;
    };

    const showEditorError = (card, message) => {
        showErrorElement(readEditorErrorElement(card), message);
    };

    const showRemoveError = (card, message) => {
        showErrorElement(readRemoveErrorElement(card), message);
    };

    const focusEditorInput = (card) => {
        const input = readPositionInput(card);
        if (!(input instanceof HTMLInputElement) || !input.isConnected) {
            return;
        }

        input.focus();
        input.select?.();
    };

    // Runs once the menu has settled: focus moves to the first enabled action only while it still sits where
    // the opening left it (the trigger, or lost to the body), so a user who moved on keeps their place.
    const focusFirstMenuAction = (card) => {
        const menu = readMenu(card);
        if (!(menu instanceof HTMLElement)) {
            return;
        }

        const active = documentRef?.activeElement ?? null;
        const focusUnmoved = active === null
            || active === documentRef?.body
            || active === menuOpenFocusOrigin
            || active === readMenuTrigger(card);
        if (!focusUnmoved) {
            return;
        }

        const firstAction = Array.from(menu.querySelectorAll('button'))
            .find((button) => button instanceof HTMLButtonElement && !button.disabled && !button.hidden);
        if (!(firstAction instanceof HTMLElement)) {
            return;
        }

        // A pointer open never shows the keyboard outline, also right after keyboard use, when the browser would
        // otherwise carry it over to programmatic focus.
        if (menuOpenedByKeyboard) {
            firstAction.focus();
        } else {
            firstAction.focus({ focusVisible: false });
        }
    };

    const readBrowserRoot = () => {
        const root = documentRef?.querySelector?.('[data-lists-manual-browser]');
        return root instanceof HTMLElement ? root : null;
    };

    const isParkingSpot = (element) => element === null
        || element === undefined
        || element === documentRef?.body
        || element?.dataset?.listsCardSheetPanel !== undefined
        || element?.dataset?.listsCardRemoveConfirmPanel !== undefined;

    /*
     * The one owner of the card's native controls (they carry `data-paged-search-busy-owner="card"`, so the
     * browser's shared lock leaves them alone): disabled while this card's write is out or the browser is
     * busy, and `Change position` while a filter is on. A focused control that gets disabled parks focus on
     * its panel (the dialog of the remove confirmation), else lets it go; once the card is usable again,
     * focus parked or lost returns to `parkedFocus` - a newer focus the user chose stays.
     */
    const syncCardControls = (card) => {
        if (!(card instanceof HTMLElement)) {
            return;
        }

        const locked = card === pendingCard || readBrowserRoot()?.dataset.loading === 'true';
        const filterActive = readOrderRoot()?.dataset.listsManualFilterActive === 'true';
        const active = documentRef?.activeElement ?? null;
        card.querySelectorAll('button, input').forEach((control) => {
            if (!(control instanceof HTMLElement) || !('disabled' in control)) {
                return;
            }

            const disabled = locked || (filterActive && control.matches('[data-lists-change-position-trigger]'));
            if (disabled && control === active && !control.disabled) {
                parkedFocus = control;
                const panel = control.closest('[data-lists-card-remove-confirm-panel]')
                    ?? control.closest('[data-lists-card-sheet-panel]');
                if (panel instanceof HTMLElement) {
                    panel.focus({ preventScroll: true });
                } else {
                    control.blur?.();
                }
            }
            control.disabled = disabled;
        });

        if (locked || !(parkedFocus instanceof HTMLElement) || !card.contains(parkedFocus)) {
            return;
        }

        const target = parkedFocus;
        parkedFocus = null;
        if (target.isConnected && target.disabled !== true && isParkingSpot(documentRef?.activeElement)) {
            target.focus({ preventScroll: true });
        }
    };

    const syncAllCardControls = () => {
        readBrowserCards().forEach(syncCardControls);
    };

    const syncSheetAria = (card, activeState) => {
        const menu = readMenu(card);
        if (menu instanceof HTMLElement) {
            menu.setAttribute('aria-hidden', activeState === 'menu' ? 'false' : 'true');
        }

        const editor = readEditor(card);
        if (editor instanceof HTMLElement) {
            editor.setAttribute('aria-hidden', activeState === 'editor' ? 'false' : 'true');
        }
    };

    const readMeasuredElementHeight = (element) => {
        if (!(element instanceof HTMLElement)) {
            return 0;
        }

        const scrollHeight = Number(element.scrollHeight ?? 0);
        if (Number.isFinite(scrollHeight) && scrollHeight > 0) {
            return scrollHeight;
        }

        const offsetHeight = Number(element.offsetHeight ?? 0);
        if (Number.isFinite(offsetHeight) && offsetHeight > 0) {
            return offsetHeight;
        }

        if (typeof element.getBoundingClientRect === 'function') {
            const rectHeight = Number(element.getBoundingClientRect()?.height ?? 0);
            if (Number.isFinite(rectHeight) && rectHeight > 0) {
                return rectHeight;
            }
        }

        return 0;
    };

    const setPanelHeight = (panel, height) => {
        if (!(panel instanceof HTMLElement) || !Number.isFinite(height) || height <= 0) {
            return;
        }

        panel.style.setProperty('height', `${Math.round(height)}px`);
    };

    const clearPanelHeight = (panel) => {
        if (!(panel instanceof HTMLElement)) {
            return;
        }

        panel.style.removeProperty('height');
    };

    const forceReflow = (element) => {
        if (!(element instanceof HTMLElement)) {
            return 0;
        }

        return Number(element.offsetHeight ?? 0);
    };

    const readPanelVerticalPadding = (panel) => {
        if (!(panel instanceof HTMLElement) || typeof globalThis.getComputedStyle !== 'function') {
            return 0;
        }

        const styles = globalThis.getComputedStyle(panel);
        if (styles === null || typeof styles !== 'object') {
            return 0;
        }

        const paddingTop = Number.parseFloat(String(styles.paddingTop ?? '0'));
        const paddingBottom = Number.parseFloat(String(styles.paddingBottom ?? '0'));

        return (Number.isFinite(paddingTop) ? paddingTop : 0)
            + (Number.isFinite(paddingBottom) ? paddingBottom : 0);
    };

    const measureSheetStateHeight = (card, state) => {
        const sheet = readSheet(card);
        const panel = readSheetPanel(card);
        const view = readSheetView(card, state);
        if (!(sheet instanceof HTMLElement) || !(panel instanceof HTMLElement) || !(view instanceof HTMLElement)) {
            return 0;
        }

        const previousMeasureState = String(sheet.dataset.sheetMeasureState ?? '').trim();
        sheet.dataset.sheetMeasureState = state;

        const measuredHeight = readMeasuredElementHeight(view) + readPanelVerticalPadding(panel);

        if (previousMeasureState === '') {
            delete sheet.dataset.sheetMeasureState;
        } else {
            sheet.dataset.sheetMeasureState = previousMeasureState;
        }

        return measuredHeight;
    };

    const readMotionConfig = (element) => {
        if (!(element instanceof HTMLElement)) {
            return {
                enabled: false,
                openDurationMs: 320,
                closeDurationMs: 220,
                switchDurationMs: 160,
                resizeDurationMs: 260,
                backdropDurationMs: 220,
                easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            };
        }

        const styles = typeof globalThis.getComputedStyle === 'function'
            ? globalThis.getComputedStyle(element)
            : null;

        return {
            enabled: canAnimateElement(element),
            openDurationMs: readCssTimeMs(element, '--lists-card-motion-open-duration', 320),
            closeDurationMs: readCssTimeMs(element, '--lists-card-motion-close-duration', 220),
            switchDurationMs: readCssTimeMs(element, '--lists-card-motion-switch-duration', 160),
            resizeDurationMs: readCssTimeMs(element, '--lists-card-motion-resize-duration', 260),
            backdropDurationMs: readCssTimeMs(element, '--lists-card-motion-backdrop-duration', 220),
            easing: styles?.getPropertyValue('--lists-card-motion-ease').trim() || 'cubic-bezier(0.22, 1, 0.36, 1)',
        };
    };

    const resetSheetInlineStyles = (card) => {
        [readSheetBackdrop(card), readSheetPanel(card), readMenu(card), readEditor(card)].forEach((element) => {
            if (!(element instanceof HTMLElement)) {
                return;
            }

            element.style.removeProperty('opacity');
            element.style.removeProperty('transform');
            element.style.removeProperty('visibility');
            element.style.removeProperty('pointer-events');
        });

        clearPanelHeight(readSheetPanel(card));
    };

    const resetConfirmInlineStyles = (card) => {
        [readRemoveConfirmBackdrop(card), readRemoveConfirmPanel(card)].forEach((element) => {
            if (!(element instanceof HTMLElement)) {
                return;
            }

            element.style.removeProperty('opacity');
            element.style.removeProperty('transform');
        });
    };

    const beginMotion = (motionMap, card, resetStyles) => {
        const currentState = motionMap.get(card) ?? { runId: 0, animations: [] };
        cancelAnimations(currentState.animations);
        currentState.animations = [];
        currentState.runId += 1;
        motionMap.set(card, currentState);
        resetStyles(card);
        return currentState.runId;
    };

    const setMotionAnimations = (motionMap, card, runId, animations) => {
        const currentState = motionMap.get(card);
        if (!currentState || currentState.runId !== runId) {
            cancelAnimations(animations);
            return false;
        }

        currentState.animations = animations.filter(Boolean);
        return true;
    };

    const isMotionCurrent = (motionMap, card, runId) => {
        const currentState = motionMap.get(card);
        return !!currentState && currentState.runId === runId;
    };

    const clearMotionAnimations = (motionMap, card, runId) => {
        const currentState = motionMap.get(card);
        if (!currentState || currentState.runId !== runId) {
            return;
        }

        currentState.animations = [];
    };

    const finishSheetState = (card, state) => {
        const sheet = readSheet(card);
        if (!(sheet instanceof HTMLElement)) {
            return;
        }

        sheet.hidden = false;
        sheet.setAttribute('aria-hidden', 'false');
        sheet.dataset.sheetState = state;
        delete sheet.dataset.sheetMeasureState;
        syncSheetAria(card, state);
        resetSheetInlineStyles(card);
        activeSheetCard = card;

        if (state === 'editor' && activeCard === card) {
            focusEditorInput(card);
        }

        if (state === 'menu' && activeMenuCard === card) {
            focusFirstMenuAction(card);
        }
    };

    const hideSheetState = (card) => {
        const sheet = readSheet(card);
        if (!(sheet instanceof HTMLElement)) {
            return;
        }

        sheet.hidden = true;
        sheet.setAttribute('aria-hidden', 'true');
        delete sheet.dataset.sheetState;
        delete sheet.dataset.sheetMeasureState;
        syncSheetAria(card, null);
        resetSheetInlineStyles(card);

        if (activeSheetCard === card) {
            activeSheetCard = null;
        }
    };

    const animateSheetOpen = async (card, state) => {
        const sheet = readSheet(card);
        const panel = readSheetPanel(card);
        const backdrop = readSheetBackdrop(card);
        const view = readSheetView(card, state);
        if (!(sheet instanceof HTMLElement) || !(panel instanceof HTMLElement) || !(view instanceof HTMLElement)) {
            finishSheetState(card, state);
            return;
        }

        const motion = readMotionConfig(panel);
        sheet.hidden = false;
        sheet.setAttribute('aria-hidden', 'false');
        sheet.dataset.sheetState = state;
        syncSheetAria(card, state);
        activeSheetCard = card;

        const targetHeight = measureSheetStateHeight(card, state);
        if (motion.enabled === false) {
            if (targetHeight > 0) {
                setPanelHeight(panel, targetHeight);
            }
            finishSheetState(card, state);
            return;
        }

        const runId = beginMotion(sheetMotionState, card, resetSheetInlineStyles);
        const startHeight = targetHeight > 0 ? Math.min(targetHeight, 76) : 76;
        setPanelHeight(panel, startHeight);
        if (backdrop instanceof HTMLElement) {
            backdrop.style.opacity = '0';
        }
        panel.style.opacity = '0';
        panel.style.transform = 'translateY(24px) scale(0.985)';
        view.style.opacity = '0';
        view.style.transform = 'translateY(16px) scale(0.985)';
        view.style.visibility = 'visible';
        view.style.pointerEvents = 'none';

        const animations = [
            playAnimation(backdrop, { opacity: [0, 1] }, {
                duration: motion.backdropDurationMs,
                easing: 'linear',
                fill: 'forwards',
            }),
            playAnimation(panel, {
                height: [`${startHeight}px`, `${Math.max(startHeight, targetHeight)}px`],
                opacity: [0, 1],
                transform: ['translateY(24px) scale(0.985)', 'translateY(0) scale(1)'],
            }, {
                duration: motion.openDurationMs,
                easing: motion.easing,
                fill: 'forwards',
            }),
            playAnimation(view, {
                opacity: [0, 1],
                transform: ['translateY(16px) scale(0.985)', 'translateY(0) scale(1)'],
            }, {
                duration: motion.openDurationMs,
                delay: Math.min(60, Math.round(motion.openDurationMs * 0.2)),
                easing: motion.easing,
                fill: 'forwards',
            }),
        ].filter(Boolean);

        if (!setMotionAnimations(sheetMotionState, card, runId, animations)) {
            return;
        }

        await settleAnimations(animations);
        if (!isMotionCurrent(sheetMotionState, card, runId)) {
            return;
        }

        clearMotionAnimations(sheetMotionState, card, runId);
        finishSheetState(card, state);
    };

    const animateSheetSwitch = async (card, nextState) => {
        const sheet = readSheet(card);
        const panel = readSheetPanel(card);
        if (!(sheet instanceof HTMLElement) || !(panel instanceof HTMLElement)) {
            finishSheetState(card, nextState);
            return;
        }

        const currentState = String(sheet.dataset.sheetState ?? '').trim();
        const currentView = readSheetView(card, currentState);
        const nextView = readSheetView(card, nextState);
        if (!(currentView instanceof HTMLElement) || !(nextView instanceof HTMLElement)) {
            finishSheetState(card, nextState);
            return;
        }

        const motion = readMotionConfig(panel);
        const startHeight = readMeasuredElementHeight(panel) || measureSheetStateHeight(card, currentState);
        const targetHeight = measureSheetStateHeight(card, nextState);

        if (motion.enabled === false) {
            if (targetHeight > 0) {
                setPanelHeight(panel, targetHeight);
            }
            finishSheetState(card, nextState);
            return;
        }

        const runId = beginMotion(sheetMotionState, card, resetSheetInlineStyles);
        currentView.style.visibility = 'visible';
        currentView.style.pointerEvents = 'none';
        const fadeOutAnimations = [
            playAnimation(currentView, {
                opacity: [1, 0],
                transform: ['translateY(0) scale(1)', 'translateY(-8px) scale(0.985)'],
            }, {
                duration: motion.switchDurationMs,
                easing: motion.easing,
                fill: 'forwards',
            }),
        ].filter(Boolean);

        if (!setMotionAnimations(sheetMotionState, card, runId, fadeOutAnimations)) {
            return;
        }

        await settleAnimations(fadeOutAnimations);
        if (!isMotionCurrent(sheetMotionState, card, runId)) {
            return;
        }

        resetSheetInlineStyles(card);
        setPanelHeight(panel, startHeight > 0 ? startHeight : targetHeight);
        forceReflow(panel);
        sheet.dataset.sheetState = nextState;
        syncSheetAria(card, nextState);
        nextView.style.opacity = '0';
        nextView.style.transform = 'translateY(16px) scale(0.985)';
        nextView.style.visibility = 'visible';
        nextView.style.pointerEvents = 'none';
        const revealDelayMs = targetHeight > startHeight
            ? Math.min(90, Math.round(motion.resizeDurationMs * 0.35))
            : 0;

        const revealAnimations = [
            playAnimation(panel, {
                height: [`${startHeight}px`, `${targetHeight}px`],
            }, {
                duration: motion.resizeDurationMs,
                easing: motion.easing,
                fill: 'forwards',
            }),
            playAnimation(nextView, {
                opacity: [0, 1],
                transform: ['translateY(16px) scale(0.985)', 'translateY(0) scale(1)'],
            }, {
                duration: motion.openDurationMs,
                delay: revealDelayMs,
                easing: motion.easing,
                fill: 'forwards',
            }),
        ].filter(Boolean);

        if (!setMotionAnimations(sheetMotionState, card, runId, revealAnimations)) {
            return;
        }

        await settleAnimations(revealAnimations);
        if (!isMotionCurrent(sheetMotionState, card, runId)) {
            return;
        }

        clearMotionAnimations(sheetMotionState, card, runId);
        finishSheetState(card, nextState);
    };

    const showSheetState = async (card, state) => {
        const sheet = readSheet(card);
        if (!(sheet instanceof HTMLElement)) {
            return;
        }

        if (sheet.hidden) {
            await animateSheetOpen(card, state);
            return;
        }

        const currentState = String(sheet.dataset.sheetState ?? '').trim();
        if (currentState === state) {
            finishSheetState(card, state);
            return;
        }

        await animateSheetSwitch(card, state);
    };

    const closeSheet = async (card, { restoreFocusTarget = null } = {}) => {
        const sheet = readSheet(card);
        const panel = readSheetPanel(card);
        const backdrop = readSheetBackdrop(card);
        if (!(sheet instanceof HTMLElement)) {
            return false;
        }

        if (sheet.hidden) {
            if (restoreFocusTarget instanceof HTMLElement && restoreFocusTarget.isConnected) {
                restoreFocusTarget.focus();
            }
            return false;
        }

        const state = String(sheet.dataset.sheetState ?? '').trim();
        const view = readSheetView(card, state);
        const motion = readMotionConfig(panel);

        if (motion.enabled === false || !(panel instanceof HTMLElement) || !(view instanceof HTMLElement)) {
            hideSheetState(card);
            if (restoreFocusTarget instanceof HTMLElement && restoreFocusTarget.isConnected) {
                restoreFocusTarget.focus();
            }
            return true;
        }

        const runId = beginMotion(sheetMotionState, card, resetSheetInlineStyles);
        const currentHeight = readMeasuredElementHeight(panel) || measureSheetStateHeight(card, state);
        if (currentHeight > 0) {
            setPanelHeight(panel, currentHeight);
        }
        view.style.visibility = 'visible';
        view.style.pointerEvents = 'none';

        const animations = [
            playAnimation(backdrop, { opacity: [1, 0] }, {
                duration: motion.backdropDurationMs,
                easing: 'linear',
                fill: 'forwards',
            }),
            playAnimation(panel, {
                opacity: [1, 0],
                transform: ['translateY(0) scale(1)', 'translateY(24px) scale(0.985)'],
            }, {
                duration: motion.closeDurationMs,
                easing: motion.easing,
                fill: 'forwards',
            }),
            playAnimation(view, {
                opacity: [1, 0],
                transform: ['translateY(0) scale(1)', 'translateY(16px) scale(0.985)'],
            }, {
                duration: motion.switchDurationMs,
                easing: motion.easing,
                fill: 'forwards',
            }),
        ].filter(Boolean);

        if (!setMotionAnimations(sheetMotionState, card, runId, animations)) {
            return false;
        }

        await settleAnimations(animations);
        if (!isMotionCurrent(sheetMotionState, card, runId)) {
            return false;
        }

        clearMotionAnimations(sheetMotionState, card, runId);
        hideSheetState(card);
        if (restoreFocusTarget instanceof HTMLElement && restoreFocusTarget.isConnected) {
            restoreFocusTarget.focus();
        }

        return true;
    };

    const finishConfirmState = (card) => {
        const confirmSurface = readRemoveConfirm(card);
        if (!(confirmSurface instanceof HTMLElement)) {
            return;
        }

        confirmSurface.hidden = false;
        confirmSurface.setAttribute('aria-hidden', 'false');
        resetConfirmInlineStyles(card);
    };

    const hideConfirmState = (card) => {
        const confirmSurface = readRemoveConfirm(card);
        if (!(confirmSurface instanceof HTMLElement)) {
            return;
        }

        confirmSurface.hidden = true;
        confirmSurface.setAttribute('aria-hidden', 'true');
        resetConfirmInlineStyles(card);
    };

    // The confirmation is a modal dialog: the shared scope traps Tab, owns Escape, locks scrolling and returns
    // focus to the card's `⋯`. `Cancel` takes focus, showing the keyboard outline only after a keyboard open.
    const activateRemoveConfirmScope = (card, panel, byKeyboard) => {
        removeConfirmScope?.deactivate({ restoreFocus: false });
        removeConfirmScope = panel instanceof HTMLElement
            ? createModalFocusScope({
                dialog: panel,
                requestClose: () => {
                    if (!mutationInFlight) {
                        void closeRemoveConfirm();
                    }
                },
                documentRef,
            })
            : null;
        if (removeConfirmScope === null) {
            return;
        }

        removeConfirmScope.activate(readMenuTrigger(card));
        const cancelButton = panel.querySelector('button[data-lists-card-remove-cancel]');
        if (cancelButton instanceof HTMLElement) {
            cancelButton.focus({ focusVisible: byKeyboard });
        }
    };

    const deactivateRemoveConfirmScope = (restoreFocus) => {
        const scope = removeConfirmScope;
        removeConfirmScope = null;
        scope?.deactivate({ restoreFocus });
    };

    const openRemoveConfirm = async (card, { byKeyboard = true } = {}) => {
        if (!(card instanceof HTMLElement) || mutationInFlight) {
            return;
        }

        const confirmSurface = readRemoveConfirm(card);
        const panel = readRemoveConfirmPanel(card);
        const backdrop = readRemoveConfirmBackdrop(card);
        if (!(confirmSurface instanceof HTMLElement)) {
            return;
        }

        if (activeMenuCard instanceof HTMLElement) {
            void closeMenu({ restoreFocus: false });
        }

        if (activeCard instanceof HTMLElement) {
            void closeEditor({ restoreFocus: false });
        }

        if (activeRemoveConfirmCard instanceof HTMLElement && activeRemoveConfirmCard !== card) {
            void closeRemoveConfirm({ restoreFocus: false });
        }

        activeRemoveConfirmCard = card;
        card.dataset.removeConfirmOpen = 'true';
        clearRemoveError(card);
        confirmSurface.hidden = false;
        confirmSurface.setAttribute('aria-hidden', 'false');
        activateRemoveConfirmScope(card, panel, byKeyboard);

        const motion = readMotionConfig(panel);
        if (motion.enabled === false || !(panel instanceof HTMLElement)) {
            finishConfirmState(card);
            return;
        }

        const runId = beginMotion(confirmMotionState, card, resetConfirmInlineStyles);
        if (backdrop instanceof HTMLElement) {
            backdrop.style.opacity = '0';
        }
        panel.style.opacity = '0';
        panel.style.transform = 'translateY(24px) scale(0.985)';

        const animations = [
            playAnimation(backdrop, { opacity: [0, 1] }, {
                duration: motion.backdropDurationMs,
                easing: 'linear',
                fill: 'forwards',
            }),
            playAnimation(panel, {
                opacity: [0, 1],
                transform: ['translateY(24px) scale(0.985)', 'translateY(0) scale(1)'],
            }, {
                duration: motion.openDurationMs,
                easing: motion.easing,
                fill: 'forwards',
            }),
        ].filter(Boolean);

        if (!setMotionAnimations(confirmMotionState, card, runId, animations)) {
            return;
        }

        await settleAnimations(animations);
        if (!isMotionCurrent(confirmMotionState, card, runId)) {
            return;
        }

        clearMotionAnimations(confirmMotionState, card, runId);
        finishConfirmState(card);
    };

    const closeRemoveConfirm = async ({ restoreFocus = true } = {}) => {
        if (!(activeRemoveConfirmCard instanceof HTMLElement)) {
            activeRemoveConfirmCard = null;
            return false;
        }

        const cardToClose = activeRemoveConfirmCard;
        const confirmSurface = readRemoveConfirm(cardToClose);
        const panel = readRemoveConfirmPanel(cardToClose);
        const backdrop = readRemoveConfirmBackdrop(cardToClose);

        if (!(confirmSurface instanceof HTMLElement)) {
            deactivateRemoveConfirmScope(false);
            activeRemoveConfirmCard = null;
            return false;
        }

        delete cardToClose.dataset.removeConfirmOpen;
        clearRemoveError(cardToClose);
        activeRemoveConfirmCard = null;
        // Focus leaves the dialog as it starts closing, so the fading panel never holds it.
        deactivateRemoveConfirmScope(restoreFocus);

        const motion = readMotionConfig(panel);
        if (motion.enabled === false || !(panel instanceof HTMLElement)) {
            hideConfirmState(cardToClose);
            return true;
        }

        const runId = beginMotion(confirmMotionState, cardToClose, resetConfirmInlineStyles);
        const animations = [
            playAnimation(backdrop, { opacity: [1, 0] }, {
                duration: motion.backdropDurationMs,
                easing: 'linear',
                fill: 'forwards',
            }),
            playAnimation(panel, {
                opacity: [1, 0],
                transform: ['translateY(0) scale(1)', 'translateY(24px) scale(0.985)'],
            }, {
                duration: motion.closeDurationMs,
                easing: motion.easing,
                fill: 'forwards',
            }),
        ].filter(Boolean);

        if (!setMotionAnimations(confirmMotionState, cardToClose, runId, animations)) {
            return false;
        }

        await settleAnimations(animations);
        if (!isMotionCurrent(confirmMotionState, cardToClose, runId)) {
            return false;
        }

        clearMotionAnimations(confirmMotionState, cardToClose, runId);
        hideConfirmState(cardToClose);

        return true;
    };

    const closeEditor = async ({ restoreFocus = true, closeSheet: shouldCloseSheet = true } = {}) => {
        if (!(activeCard instanceof HTMLElement)) {
            activeCard = null;
            return false;
        }

        const cardToClose = activeCard;
        const input = readPositionInput(cardToClose);
        const trigger = readMenuTrigger(cardToClose);

        delete cardToClose.dataset.editing;
        clearEditorError(cardToClose);

        if (input instanceof HTMLInputElement) {
            input.value = String(cardToClose.dataset.gamePosition ?? '');
        }

        activeCard = null;

        if (!shouldCloseSheet) {
            return true;
        }

        return closeSheet(cardToClose, {
            restoreFocusTarget: restoreFocus ? trigger : null,
        });
    };

    const closeMenu = async ({ restoreFocus = true, closeSheet: shouldCloseSheet = true } = {}) => {
        if (!(activeMenuCard instanceof HTMLElement)) {
            activeMenuCard = null;
            return false;
        }

        const cardToClose = activeMenuCard;
        const trigger = readMenuTrigger(cardToClose);

        delete cardToClose.dataset.menuOpen;
        activeMenuCard = null;

        if (trigger instanceof HTMLElement) {
            trigger.setAttribute('aria-expanded', 'false');
        }

        if (!shouldCloseSheet) {
            return true;
        }

        return closeSheet(cardToClose, {
            restoreFocusTarget: restoreFocus ? trigger : null,
        });
    };

    const openMenu = (card, { byKeyboard = true } = {}) => {
        if (!(card instanceof HTMLElement) || mutationInFlight) {
            return;
        }

        const menu = readMenu(card);
        const trigger = readMenuTrigger(card);
        if (!(menu instanceof HTMLElement) || !(trigger instanceof HTMLElement)) {
            return;
        }

        if (activeCard instanceof HTMLElement) {
            void closeEditor({
                restoreFocus: false,
                closeSheet: activeCard !== card,
            });
        }

        if (activeRemoveConfirmCard instanceof HTMLElement) {
            void closeRemoveConfirm({ restoreFocus: false });
        }

        if (activeMenuCard instanceof HTMLElement && activeMenuCard !== card) {
            void closeMenu({ restoreFocus: false });
        }

        activeMenuCard = card;
        menuOpenFocusOrigin = documentRef?.activeElement ?? null;
        menuOpenedByKeyboard = byKeyboard;
        card.dataset.menuOpen = 'true';
        trigger.setAttribute('aria-expanded', 'true');
        void showSheetState(card, 'menu');
    };

    const openEditor = (card) => {
        if (!(card instanceof HTMLElement) || mutationInFlight) {
            return;
        }

        const orderRoot = readOrderRoot();
        const filterActive = orderRoot?.dataset.listsManualFilterActive === 'true';
        if (filterActive) {
            return;
        }

        const editor = readEditor(card);
        const input = readPositionInput(card);
        if (!(editor instanceof HTMLElement) || !(input instanceof HTMLInputElement)) {
            return;
        }

        if (activeMenuCard instanceof HTMLElement) {
            void closeMenu({
                restoreFocus: false,
                closeSheet: activeMenuCard !== card,
            });
        }

        if (activeRemoveConfirmCard instanceof HTMLElement) {
            void closeRemoveConfirm({ restoreFocus: false });
        }

        if (activeCard instanceof HTMLElement && activeCard !== card) {
            void closeEditor({ restoreFocus: false });
        }

        activeCard = card;
        card.dataset.editing = 'true';
        clearEditorError(card);
        delete card.dataset.menuOpen;
        activeMenuCard = null;
        void showSheetState(card, 'editor');
    };

    const normalizePosition = (rawValue) => {
        const orderRoot = readOrderRoot();
        const totalCount = Number.parseInt(String(orderRoot?.dataset.listsManualTotalCount ?? ''), 10);
        const normalizedTotalCount = Number.isFinite(totalCount) && totalCount > 0 ? totalCount : 1;
        const nextPosition = Number.parseInt(String(rawValue ?? '').trim(), 10);

        if (!Number.isFinite(nextPosition)) {
            return null;
        }

        return Math.min(normalizedTotalCount, Math.max(1, nextPosition));
    };

    const readVisiblePositionRange = () => {
        const positions = readVisibleCards()
            .map((card) => Number.parseInt(String(card.dataset.gamePosition ?? ''), 10))
            .filter((position) => Number.isFinite(position));

        if (positions.length === 0) {
            return null;
        }

        return {
            first: Math.min(...positions),
            last: Math.max(...positions),
        };
    };

    const readCurrentPage = () => {
        const marker = documentRef?.querySelector?.('[data-lists-manual-browser] [data-paged-search-current-page]');
        const page = Number.parseInt(String(marker?.dataset?.pagedSearchCurrentPage ?? ''), 10);

        return Number.isInteger(page) && page > 0 ? page : 1;
    };

    // Runs after the swap; leaves focus alone when the user has already put it somewhere on the page. A card the
    // swap did not replace (the reload failed) keeps focus on its own `⋯`.
    const focusAfterRemove = (card, removedIndex, previousPage, byKeyboard) => {
        const active = documentRef?.activeElement ?? null;
        if (active !== null && active !== documentRef?.body && active.isConnected) {
            return;
        }

        if (card.isConnected) {
            readMenuTrigger(card)?.focus({ focusVisible: byKeyboard });
            return;
        }

        const emptyStateAction = documentRef?.querySelector?.('[data-lists-manual-browser] .lists-empty-add-game');
        const target = resolveRemoveSuccessorFocus({
            removedIndex,
            previousPage,
            currentPage: readCurrentPage(),
            cards: readBrowserCards(),
            emptyStateAction: emptyStateAction instanceof HTMLElement ? emptyStateAction : null,
        });
        if (target instanceof HTMLElement) {
            target.focus({ focusVisible: byKeyboard });
        }
    };

    // `swapped`, `failed` or `stale` (a newer read took over: it says nothing about this write).
    const reloadBrowserSurface = async () => {
        if (!manualGamesBrowserController || typeof manualGamesBrowserController.reload !== 'function') {
            return 'failed';
        }

        return manualGamesBrowserController.reload('replace', {
            source: 'external-mutation',
        });
    };

    const submitMove = async (card, nextPosition) => {
        const orderRoot = readOrderRoot();
        const reorderUrl = String(orderRoot?.dataset.listsManualReorderUrl ?? '').trim();
        const gameId = String(card?.dataset.gameId ?? '').trim();
        const currentPosition = Number.parseInt(String(card?.dataset.gamePosition ?? ''), 10);

        if (!(card instanceof HTMLElement) || reorderUrl === '' || gameId === '' || !Number.isFinite(currentPosition)) {
            return;
        }

        if (nextPosition === currentPosition) {
            closeEditor();
            return;
        }

        const visibleRange = readVisiblePositionRange();
        const staysOnCurrentPage = visibleRange !== null
            && nextPosition >= visibleRange.first
            && nextPosition <= visibleRange.last;

        mutationInFlight = true;
        pendingCard = card;
        closeMenu({ restoreFocus: false });
        clearEditorError(card);
        card.dataset.mutationState = staysOnCurrentPage ? 'moving-local' : 'moving-away';
        syncCardControls(card);
        const releaseBusy = acquirePagedSearchBusy(readBrowserRoot(), 'external-mutation');
        try {
            const result = await sendJsonMutation(fetchImpl, {
                url: reorderUrl,
                csrfToken,
                body: {
                    game_id: gameId,
                    position: nextPosition,
                },
                fallbackErrorMessage: 'Failed to save manual list order.',
                unknownErrorMessage: MOVE_UNKNOWN_MESSAGE,
            });

            if (!result.ok) {
                showEditorError(card, result.errorMessage);
                // The editor stays open with the typed position; focus goes back to it unless moved on.
                parkedFocus = readPositionInput(card);
                return;
            }

            parkedFocus = null;
            pendingCard = null;
            delete card.dataset.mutationState;
            await closeEditor({ restoreFocus: false });
            const outcome = await reloadBrowserSurface();

            if (outcome === 'failed') {
                showToast(MOVE_REFRESH_MESSAGE);
            } else if (outcome === 'swapped' && !staysOnCurrentPage) {
                showToast(`Moved to #${nextPosition}`);
            }
        } finally {
            pendingCard = null;
            delete card.dataset.mutationState;
            mutationInFlight = false;
            releaseBusy();
            syncCardControls(card);
        }
    };

    const submitRemove = async (card, { byKeyboard = true } = {}) => {
        const orderRoot = readOrderRoot();
        const removeUrl = String(orderRoot?.dataset.listsManualRemoveUrl ?? '').trim();
        const gameId = String(card?.dataset.gameId ?? '').trim();

        if (!(card instanceof HTMLElement) || removeUrl === '' || gameId === '') {
            return;
        }

        const removedIndex = readBrowserCards().indexOf(card);
        let removed = false;
        const previousPage = readCurrentPage();
        mutationInFlight = true;
        pendingCard = card;
        clearRemoveError(card);
        card.dataset.mutationState = 'removing';
        syncCardControls(card);
        const releaseBusy = acquirePagedSearchBusy(readBrowserRoot(), 'external-mutation');
        try {
            const result = await sendJsonMutation(fetchImpl, {
                url: removeUrl,
                csrfToken,
                body: {
                    game_id: gameId,
                },
                fallbackErrorMessage: 'Failed to remove game from manual list.',
                unknownErrorMessage: REMOVE_UNKNOWN_MESSAGE,
            });

            if (!result.ok) {
                showRemoveError(card, result.errorMessage);
                // The confirmation stays open; focus goes back to its `Remove` unless moved on in it.
                parkedFocus = card.querySelector('[data-lists-remove-confirm]');
                return;
            }

            parkedFocus = null;
            pendingCard = null;
            delete card.dataset.mutationState;
            await closeRemoveConfirm({ restoreFocus: false });
            await closeEditor({ restoreFocus: false });
            const outcome = await reloadBrowserSurface();
            removed = true;
            if (outcome === 'failed') {
                showToast(REMOVE_REFRESH_MESSAGE);
            } else if (outcome === 'swapped') {
                showToast('Removed from list');
            }
        } finally {
            pendingCard = null;
            delete card.dataset.mutationState;
            mutationInFlight = false;
            releaseBusy();
            syncCardControls(card);
        }

        // After the busy state lets go: it keeps the swapped-in controls disabled until then.
        if (removed) {
            focusAfterRemove(card, removedIndex, previousPage, byKeyboard);
        }
    };

    documentRef?.addEventListener?.('click', (event) => {
        const target = event.target;
        if (target === null || typeof target !== 'object' || typeof target.closest !== 'function') {
            return;
        }

        const menuTrigger = target.closest('[data-lists-card-menu-trigger]');
        if (menuTrigger instanceof HTMLElement) {
            const card = menuTrigger.closest('[data-lists-manual-card]');
            if (card instanceof HTMLElement) {
                event.preventDefault();
                if (activeMenuCard === card) {
                    closeMenu();
                } else {
                    // A pointer click reports its click count in `detail`; Enter / Space report 0.
                    openMenu(card, { byKeyboard: !(Number(event.detail) > 0) });
                }
            }

            return;
        }

        const changePositionTrigger = target.closest('[data-lists-change-position-trigger]');
        if (changePositionTrigger instanceof HTMLElement) {
            if (changePositionTrigger.hasAttribute('disabled')) {
                return;
            }

            const card = changePositionTrigger.closest('[data-lists-manual-card]');
            if (card instanceof HTMLElement) {
                event.preventDefault();
                openEditor(card);
            }

            return;
        }

        const cancelTrigger = target.closest('[data-lists-edit-cancel]');
        if (cancelTrigger instanceof HTMLElement) {
            event.preventDefault();
            closeEditor();
            return;
        }

        const menuDismissTrigger = target.closest('[data-lists-card-menu-dismiss]');
        if (menuDismissTrigger instanceof HTMLElement) {
            event.preventDefault();
            closeMenu();
            return;
        }

        const removeTrigger = target.closest('[data-lists-remove-trigger]');
        if (removeTrigger instanceof HTMLElement) {
            const card = removeTrigger.closest('[data-lists-manual-card]');
            if (card instanceof HTMLElement) {
                event.preventDefault();
                // A pointer click reports its click count in `detail`; Enter / Space report 0.
                void openRemoveConfirm(card, { byKeyboard: !(Number(event.detail) > 0) });
            }

            return;
        }

        const removeConfirmCancelTrigger = target.closest('[data-lists-card-remove-cancel]');
        if (removeConfirmCancelTrigger instanceof HTMLElement) {
            event.preventDefault();
            closeRemoveConfirm();
            return;
        }

        const removeConfirmTrigger = target.closest('[data-lists-remove-confirm]');
        if (removeConfirmTrigger instanceof HTMLElement) {
            const card = removeConfirmTrigger.closest('[data-lists-manual-card]');
            if (card instanceof HTMLElement) {
                event.preventDefault();
                return submitRemove(card, { byKeyboard: !(Number(event.detail) > 0) });
            }

            return;
        }

        if (activeMenuCard instanceof HTMLElement && target instanceof HTMLElement && !target.closest('[data-lists-card-menu]')) {
            closeMenu({ restoreFocus: false });
        }
    });

    // The confirmation covers only its card, so pagination or the filter can swap the results under it. A
    // confirmation whose card left the page lets go of the modal scope (scroll lock, Tab trap) without moving
    // focus, which stays on the control the user used.
    const releaseDetachedRemoveConfirm = () => {
        const card = activeRemoveConfirmCard;
        if (!(card instanceof HTMLElement) || card.isConnected) {
            return;
        }

        cancelAnimations(confirmMotionState.get(card)?.animations ?? []);
        confirmMotionState.delete(card);
        activeRemoveConfirmCard = null;
        deactivateRemoveConfirmScope(false);
    };

    documentRef?.addEventListener?.('paged-search:swap', () => {
        releaseDetachedRemoveConfirm();
        syncAllCardControls();
    });
    documentRef?.addEventListener?.('paged-search:busychange', () => {
        releaseDetachedRemoveConfirm();
        syncAllCardControls();
    });

    documentRef?.addEventListener?.('submit', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLFormElement)) {
            return;
        }

        if (!(target.matches?.('[data-lists-card-editor-form]') ?? false)) {
            return;
        }

        event.preventDefault();

        const card = target.closest('[data-lists-manual-card]');
        const input = card instanceof HTMLElement ? readPositionInput(card) : null;
        if (!(card instanceof HTMLElement) || !(input instanceof HTMLInputElement)) {
            return;
        }

        const nextPosition = normalizePosition(input.value);
        if (nextPosition === null) {
            return;
        }

        input.value = String(nextPosition);
        return submitMove(card, nextPosition);
    });

    documentRef?.addEventListener?.('keydown', (event) => {
        if (event.key !== 'Escape' || mutationInFlight) {
            return;
        }

        if (activeCard instanceof HTMLElement) {
            event.preventDefault?.();
            closeEditor();
            return;
        }

        if (activeMenuCard instanceof HTMLElement) {
            event.preventDefault?.();
            closeMenu();
        }
    });
}
